import {
  Input,
  Select,
  SelectOption,
  TextCaption,
} from '@pypestream/design-system';
import { valueOf } from '@pypestream/ui-patterns';

import type { OrgSecretScope } from './maestro-org-api';
import type { SecretFilters } from './org-secret-rows';
import { isScopeTarget, type ScopeOption } from './org-secret-scope';
import { scopeLabel, useSecretsText } from './org-secrets-text';

const optionScope = (option: ScopeOption): OrgSecretScope => {
  const named = { id: 0, name: option.name };
  return {
    type: option.kind,
    project: option.kind === 'project' ? named : null,
    application: option.kind === 'application' ? named : null,
    agent: option.kind === 'agent' ? named : null,
  };
};

/** "Agent: triage (Project A)": one label for a scope option, in the filter and the form. */
export function useScopeOptionLabel() {
  const text = useSecretsText();
  return (option: ScopeOption) => {
    const label = scopeLabel(text, optionScope(option));
    return option.project ? `${label} (${option.project})` : label;
  };
}

export function OrgSecretsToolbar({
  filters,
  onChange,
  scopes,
  orgName,
  hasParentRows,
  counts,
}: {
  filters: SecretFilters;
  onChange: (filters: SecretFilters) => void;
  scopes: ScopeOption[];
  orgName: string;
  hasParentRows: boolean;
  counts: { secrets: number; variables: number };
}) {
  const text = useSecretsText();
  const optionLabel = useScopeOptionLabel();

  return (
    <div className="c-org-toolbar">
      <Input
        className="c-org-toolbar__search"
        variant="outlined"
        type="search"
        aria-label={text('search')}
        placeholder={text('search')}
        value={filters.search}
        test-id="org-secrets-search"
        onInput={(e) => onChange({ ...filters, search: valueOf(e) })}
      />
      <Select
        variant="outlined"
        aria-label={text('filters.type')}
        value={filters.type}
        test-id="org-secrets-type"
        onChange={(e) =>
          onChange({ ...filters, type: valueOf(e) as SecretFilters['type'] })
        }
      >
        {(['all', 'secret', 'variable'] as const).map((type) => (
          <SelectOption key={type} value={type}>
            {text(`filters.types.${type}`)}
          </SelectOption>
        ))}
      </Select>
      <Select
        variant="outlined"
        aria-label={text('filters.scope')}
        value={filters.scope}
        test-id="org-secrets-scope"
        onChange={(e) => {
          const value = valueOf(e);
          onChange({
            ...filters,
            scope: isScopeTarget(value, scopes) ? value : 'all',
          });
        }}
      >
        <SelectOption value="all">{text('filters.scopes.all')}</SelectOption>
        {scopes.map((option) => (
          <SelectOption key={option.value} value={option.value}>
            {optionLabel(option)}
          </SelectOption>
        ))}
      </Select>
      <Select
        variant="outlined"
        aria-label={text('filters.source')}
        value={filters.source}
        test-id="org-secrets-source"
        onChange={(e) =>
          onChange({
            ...filters,
            source: valueOf(e) as SecretFilters['source'],
          })
        }
      >
        <SelectOption value="all">{text('filters.sources.all')}</SelectOption>
        <SelectOption value="own">{orgName}</SelectOption>
        <SelectOption value="pypestreamKey">
          {text('filters.sources.pypestreamKey')}
        </SelectOption>
        {hasParentRows && (
          <SelectOption value="parent">
            {text('filters.sources.parent')}
          </SelectOption>
        )}
      </Select>
      <TextCaption
        className="c-org-toolbar__count"
        variant="secondary"
        test-id="org-secrets-count"
      >
        {text('count', {
          secrets: text('secretsCount', { count: counts.secrets }),
          variables: text('variablesCount', { count: counts.variables }),
        })}
      </TextCaption>
    </div>
  );
}
